import { useLazyQuery } from "@apollo/client";
import LinkOff from "@mui/icons-material/LinkOff";
import { Box, Button, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { CountryPicker } from "components/Facility/CountryPicker";
import { FacilityPicker } from "components/FacilityPicker";
import { FormTextField } from "components/Forms/FormTextField";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { StatePicker } from "components/StatePicker";
import { FacilityPickerFragment } from "generated-graphql/graphql";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { useFormContext, UseFormReturn, useFieldArray } from "react-hook-form";
import { useParams } from "react-router-dom";

import { SubRecordDialog, SubRecordGrid } from "../subRecordListEditor";
import { SubRecordRow, useSubRecordEditor } from "../useSubRecordEditor";
import {
  CONSOLIDATED_SITE_FACILITY_PREFILL,
  emptyConsolidatedSite,
  facilityToConsolidatedSite,
} from "./buildInputs";
import { gridSx, YES_NO_OPTIONS } from "./constants";
import { SectionHeader } from "./SectionHeader";
import { ConsolidatedSiteValue, FilingNotificationsFormData } from "./types";

/**
 * §14 — LQG Consolidation of VSQG Hazardous Waste. A gated list of the VSQG sites
 * whose hazardous waste this LQG consolidates. The gate (`isLqgConsolidator`)
 * reveals a grid of saved sites when Yes; when not Yes, the grid is hidden and
 * `consolidatedSites` is cleared to `[]` at save time (see
 * `buildConsolidationInput`). The gate persists on the facility RCRA profile;
 * the sites are submission-scoped and persist via
 * `upsertSubmissionRcraConsolidation`.
 */
export function LqgConsolidationSection({ facilityId }: { facilityId: string }) {
  const { control, metadata } = useValidatingFormContext<FilingNotificationsFormData>();
  const isDisplayOnly = metadata?.isDisplayOnly ?? false;
  const { tenantId = "" } = useParams<{ tenantId: string }>();
  const { watch, getValues } = useFormContext<FilingNotificationsFormData>();
  const isConsolidator = watch("isLqgConsolidator");
  const sites = watch("consolidatedSites") ?? [];

  const { append, remove, update } = useFieldArray<
    FilingNotificationsFormData,
    "consolidatedSites"
  >({
    control,
    name: "consolidatedSites",
  });

  const editor = useSubRecordEditor<ConsolidatedSiteValue>({
    items: sites,
    append: (value) => append(value),
    update: (index, value) => update(index, value),
    remove,
    getItem: (index) => getValues(`consolidatedSites.${index}`),
  });

  const columns: GridColDef<SubRecordRow<ConsolidatedSiteValue>>[] = [
    {
      field: "epaIdNumber",
      headerName: "EPA ID",
      flex: 1,
      valueGetter: (_, row) => row.epaIdNumber ?? "",
    },
    {
      field: "siteName",
      headerName: "Site Name",
      flex: 1.5,
      valueGetter: (_, row) => row.siteName ?? "",
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      sortable: false,
      valueGetter: (_, row) => [row.city, row.state].filter(Boolean).join(", "),
    },
    {
      field: "contact",
      headerName: "Contact",
      flex: 1,
      sortable: false,
      valueGetter: (_, row) => [row.contactFirst, row.contactLast].filter(Boolean).join(" "),
    },
  ];

  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="LQG Consolidation of VSQG Hazardous Waste (§14)" />
      </Box>
      <Box sx={{ gridColumn: "span 12" }}>
        <RadioGroupField
          label="Is this site an LQG consolidating VSQG hazardous waste?"
          name="isLqgConsolidator"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
      {isConsolidator === true && (
        <SubRecordGrid<ConsolidatedSiteValue>
          rows={editor.rows}
          dataColumns={columns}
          listLabel="Consolidated VSQG sites"
          addLabel="Add consolidated site"
          isDisplayOnly={isDisplayOnly}
          onAdd={editor.openAdd}
          onEdit={editor.openEdit}
          rowToDelete={editor.rowToDelete}
          onRequestDelete={editor.requestDelete}
          onConfirmDelete={editor.confirmDelete}
          deleteHeader="Remove this consolidated site?"
          renderDeleteLabel={(row) => row.siteName ?? row.epaIdNumber ?? "Unnamed site"}
        />
      )}
      {editor.isOpen && (
        <SubRecordDialog<ConsolidatedSiteValue>
          title={editor.isAdding ? "Add consolidated site" : "Edit consolidated site"}
          gridSx={gridSx}
          initialValues={editor.editingValues ?? emptyConsolidatedSite()}
          isDisplayOnly={isDisplayOnly}
          onCancel={editor.close}
          onCommit={editor.commit}
          renderFields={(dialogForm) => (
            <ConsolidatedSiteFields
              form={dialogForm}
              showFacilityPicker={editor.isAdding && !isDisplayOnly}
              ownFacilityId={facilityId}
              tenantId={tenantId}
              isDisplayOnly={isDisplayOnly}
            />
          )}
        />
      )}
    </Box>
  );
}

/**
 * Fields for the consolidated-site draft dialog. Text fields bind to the draft
 * form (`form.control`); the address pickers read the surrounding form context
 * and take the untyped control. The "prefill from a facility" picker resets the
 * draft (never the parent) — so, like every field here, nothing reaches the
 * parent form until the dialog is saved.
 */
function ConsolidatedSiteFields({
  form,
  showFacilityPicker,
  ownFacilityId,
  tenantId,
  isDisplayOnly,
}: {
  form: UseFormReturn<ConsolidatedSiteValue>;
  showFacilityPicker: boolean;
  ownFacilityId: string;
  tenantId: string;
  isDisplayOnly: boolean;
}) {
  const { control } = form;
  // The address pickers read the surrounding (draft) form context and expect an
  // untyped `Control<FieldValues>`.
  const { control: untypedControl } = useFormContext();

  const [fetchFacility, { loading }] = useLazyQuery(CONSOLIDATED_SITE_FACILITY_PREFILL);

  // A site whose contact is linked to a roster Person (via facility prefill)
  // locks the contact fields; "Use a different contact" clears the link and the
  // free-text values so they can be typed manually.
  const contactPersonId = form.watch("contactPersonId");
  const isContactLocked = contactPersonId != null;
  const handleUnlinkContact = () => {
    const opts = { shouldDirty: true, shouldValidate: true } as const;
    form.setValue("contactPersonId", null, opts);
    form.setValue("contactFirst", null, opts);
    form.setValue("contactLast", null, opts);
    form.setValue("contactPhone", null, opts);
    form.setValue("contactEmail", null, opts);
  };

  const handleFacilitySelected = async (facility: FacilityPickerFragment | null) => {
    if (!facility) {
      return;
    }
    const { data } = await fetchFacility({ variables: { facilityId: facility.id } });
    if (data?.facility) {
      // Prefill the draft only, preserving the row's sortOrder.
      const current = form.getValues();
      form.reset(facilityToConsolidatedSite(data.facility, current.sortOrder ?? null));
    }
  };

  return (
    <>
      {showFacilityPicker && (
        <Box sx={{ gridColumn: "span 12", mb: 1 }}>
          <FacilityPicker
            label="Prefill from one of your facilities"
            facilityQueryVariant="waste"
            defaultSearchTerm={`tenantId:${tenantId}`}
            disabledFacilitiesById={[ownFacilityId]}
            disabledOptionLabel="(this facility)"
            onChange={handleFacilitySelected}
            disabled={loading}
            showErrors={false}
            invalid={false}
            isTouched={false}
            isDirty={false}
            isValidating={false}
            error={undefined}
          />
        </Box>
      )}
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField name="epaIdNumber" label="Consolidated site EPA ID" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField name="siteName" label="Consolidated site name" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField name="streetAddress1" label="Street address" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField name="streetAddress2" label="Street address 2" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormTextField name="city" label="City" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <StatePicker
          name="state"
          countryFieldName="country"
          control={untypedControl}
          disabled={isDisplayOnly}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormTextField name="zipCode" label="Zip code" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <CountryPicker name="country" control={untypedControl} />
      </Box>
      <Box sx={{ gridColumn: "span 12", mt: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <SectionHeader label="Contact" />
          {!isDisplayOnly && isContactLocked && (
            <Button size="small" startIcon={<LinkOff />} onClick={handleUnlinkContact}>
              Use a different contact
            </Button>
          )}
        </Stack>
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField
          name="contactFirst"
          label="Contact first name"
          control={control}
          disabled={isContactLocked}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField
          name="contactLast"
          label="Contact last name"
          control={control}
          disabled={isContactLocked}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField
          name="contactPhone"
          label="Contact phone"
          control={control}
          disabled={isContactLocked}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField
          name="contactEmail"
          label="Contact email"
          control={control}
          disabled={isContactLocked}
        />
      </Box>
    </>
  );
}
